// 학생 스케줄 페이지

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SchoolIcon from '@mui/icons-material/School';
import { useStudentAuth } from '../hooks/useStudentAuth';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface ScheduleItem {
  day_of_week: number;
  start_time: string;
  end_time: string;
  is_active: boolean;
}

interface AcademyScheduleItem {
  id: string;
  academy_name: string;
  days_of_week: number[];
  start_time: string;
  end_time: string;
  travel_minutes: number;
  grace_minutes: number;
  is_active: boolean;
}

const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

export default function SchedulePage() {
  const { student } = useStudentAuth();
  const [schedules, setSchedules] = useState<ScheduleItem[]>([]);
  const [academySchedules, setAcademySchedules] = useState<AcademyScheduleItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchSchedules();
  }, [student]);

  const fetchSchedules = async () => {
    if (!student?.id) return;

    try {
      setIsLoading(true);
      const token = localStorage.getItem('student_access_token');

      const response = await fetch(`${API_BASE_URL}/api/v1/student-schedules/${student.id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setSchedules(data.schedules || []);
        setAcademySchedules(data.academy_schedules || []);
      }
    } catch (error) {
      console.error('스케줄 조회 에러:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 요일별로 그룹화
  const schedulesByDay = schedules.reduce((acc, schedule) => {
    const day = schedule.day_of_week;
    if (!acc[day]) acc[day] = [];
    acc[day].push(schedule);
    return acc;
  }, {} as Record<number, ScheduleItem[]>);

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        스케줄
      </Typography>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CalendarMonthIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              주간 스케줄
            </Typography>
          </Box>

          {isLoading ? (
            <Typography>로딩 중...</Typography>
          ) : Object.keys(schedulesByDay).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                등록된 스케줄이 없습니다.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                스케줄 등록은 관리자에게 문의하세요.
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>요일</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>시작</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>종료</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>상태</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[1, 2, 3, 4, 5, 6, 0].map((day) => {
                    const daySchedules = schedulesByDay[day];
                    if (!daySchedules || daySchedules.length === 0) {
                      return (
                        <TableRow key={day}>
                          <TableCell>
                            <Typography fontWeight={600}>{dayNames[day]}요일</Typography>
                          </TableCell>
                          <TableCell colSpan={3}>
                            <Typography color="text.secondary" variant="body2">
                              스케줄 없음
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    }

                    return daySchedules.map((schedule, idx) => (
                      <TableRow key={`${day}-${idx}`}>
                        {idx === 0 && (
                          <TableCell rowSpan={daySchedules.length}>
                            <Typography fontWeight={600}>{dayNames[day]}요일</Typography>
                          </TableCell>
                        )}
                        <TableCell>{schedule.start_time}</TableCell>
                        <TableCell>{schedule.end_time}</TableCell>
                        <TableCell>
                          <Chip
                            label={schedule.is_active ? '활성' : '비활성'}
                            color={schedule.is_active ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* 학원 스케줄 */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SchoolIcon color="secondary" />
            <Typography variant="h6" fontWeight={600}>
              학원 스케줄
            </Typography>
          </Box>

          {isLoading ? (
            <Typography>로딩 중...</Typography>
          ) : academySchedules.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                등록된 학원 스케줄이 없습니다.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {academySchedules.map((academy) => (
                <Paper key={academy.id} elevation={1} sx={{ p: 2 }}>
                  <Typography variant="subtitle1" fontWeight={600} color="primary">
                    {academy.academy_name}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                    {academy.days_of_week.map((day) => (
                      <Chip
                        key={day}
                        label={dayNames[day]}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    수업 시간: {academy.start_time} ~ {academy.end_time}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    이동 시간: {academy.travel_minutes}분 / 여유 시간: {academy.grace_minutes}분
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* 안내 */}
      <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          스케줄 변경이 필요하신 경우 독서실 관리자 또는 부모님께 문의해주세요.
        </Typography>
      </Box>
    </Box>
  );
}
